<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useTheme } from 'vuetify'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'


const props = defineProps({
  index: {},
  item: {},
  dirtyItems: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['show', 'add-dirty'])

const store = useStore()

const theme = useTheme()

const dirtyItems = computed(() => props.dirtyItems)
const { addDirty, getDirtyClass } = dirtyFieldMixin(dirtyItems)

const primaryColor = computed(() => theme.current.value.colors.primary)

const options = computed(() => store.state.topoProfilePoint.options)
const items = computed(() => store.state.topoProfilePoint.items)
const currentTopoProfilePoint = computed(
  () => store.state.topoProfilePoint.currentItem
)
const isLocked = computed(() => store.getters['stabSchematisation/isLockedCurrent'])

const setCurrentItem = (item) => {
  store.dispatch('topoProfilePoint/setCurrentItem', item)
}

const setNestedParameter = (payload) =>
  store.dispatch('topoProfilePoint/setNestedParameter', payload)

const sortItemsX = () => store.dispatch('topoProfilePoint/sortItemsX')

const slope = computed(() => {
  const x1 = props.item.geometry.coordinates[0]
  const y1 = props.item.geometry.coordinates[1]
  const x2 = items.value[props.index + 1]?.geometry.coordinates[0]
  const y2 = items.value[props.index + 1]?.geometry.coordinates[1]

  if (x2 - x1 !== 0 && y2 - y1 !== 0) {
    let decimal = 1 / ((y2 - y1) / (x2 - x1))
    const roundValue = 10
    const maxSlope = 30
    decimal = Math.round(decimal * roundValue) / roundValue
    return decimal >= maxSlope || -1 * decimal >= maxSlope ? false : decimal.toFixed(1)
  }

  return false
})

const onNameChanged = async ({ value, item }) => {
  const nameToSwap = item.properties.name
  await setNestedParameter({ value, item, parameter: 'properties.name' })
  addDirty(item, 'name')

  const itemToSwapName =
    item.properties.name &&
    items.value.find(
      (storeItem) => storeItem.id !== item.id && storeItem.properties.name === item.properties.name
    )

  if (itemToSwapName) {
    await setNestedParameter({
      value: nameToSwap,
      item: itemToSwapName,
      parameter: 'properties.name'
    })
    emit('add-dirty', itemToSwapName, 'name')
  }

}
</script>
<template>
  <tr
      @click="setCurrentItem(item)"
      @contextmenu.prevent="setCurrentItem(item); emit('show', $event)"
  >
    <td class="pl-4"
      :style="`border-left: solid 5px ${currentTopoProfilePoint === item ? primaryColor : 'white'}`">
      <v-text-field
          full-width
          :class="`${getDirtyClass(item, '0')} pa-0 mt-0 custom text-field ${isLocked ?'disable-events':null}`"
          hide-details
          solo
          variant="plain"
          density="compact"
          type="number"
          @update:model-value="$event ? setNestedParameter({value: $event, item: item, parameter: 'geometry.coordinates.0'}): null; emit('add-dirty', item, '0')"
          @change="sortItemsX()"
          :model-value="item.geometry.coordinates[0]"></v-text-field>

    </td>
    <td class="pl-4">
      <v-text-field
          full-width
          :class="`${getDirtyClass(item, '1')} pa-0 mt-0 custom text-field ${isLocked ?'disable-events':null}`"
          hide-details
          solo
          variant="plain"
          density="compact"
          type="number"
          @update:model-value="$event ? setNestedParameter({value: $event, item: item, parameter: 'geometry.coordinates.1'}) : null; emit('add-dirty', item, '1')"
          :model-value="item.geometry.coordinates[1]"></v-text-field>

    </td>
    <td disabled class="pl-4">
      <div v-if="slope">
        <div><span v-if="slope <0">-</span><sup>1</sup>&frasl;<sub>{{ Math.abs(slope) }}</sub></div>
      </div>
      <div v-else>
        -
      </div>

    </td>
    <td>
      <v-select
          v-if="options"
          :class="`${getDirtyClass(item, 'name')} pa-0 mt-0 custom ${isLocked ?'disable-events':null}`"
          hide-selected
          clearable
          clear-icon="mdi-close"
          :items="options.name.choices"
          :model-value="item.properties.name"
          @update:model-value="onNameChanged({item, value: $event})"
          item-title="display_name"
          density="compact"
          style="border-radius: 0; font-size: 14px"
          item-disabled="disabled"
          hide-details
          solo
          variant="plain"
      >
      </v-select>
    </td>
  </tr>
</template>
<style scoped>
.custom input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}

</style>
